import { useEffect, useRef, useState } from "react";
import * as Tus from "tus-js-client";

interface UploadVideoOptions {
  onError?: (err: Error | Tus.DetailedError) => void;
  onSuccess?: () => void;
}

export default function useVideoUpload() {
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingVideoProgress, setUploadingVideoProgress] = useState<number | null>(null);

  const uploadRef = useRef<Tus.Upload | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      uploadRef.current?.abort();
    };
  }, []);

  const uploadVideo = async (
    signedEndpoint: string,
    videoFile: File,
    options?: UploadVideoOptions
  ) => {
    setUploadingVideo(true);
    setUploadingVideoProgress(0);

    // Remove tus version
    const cleanSignedEndpoint = signedEndpoint.replace("tusv2=true", "");
    
    const upload = new Tus.Upload(videoFile, {
      endpoint: cleanSignedEndpoint,
      chunkSize: 50 * 1024 * 1024, // 50MB
      retryDelays: [0, 3000, 5000, 10000, 20000],
      removeFingerprintOnSuccess: true,
      

      onError(error) {
        if (!isMountedRef.current) return;
        setUploadingVideo(false);
        setUploadingVideoProgress(null);
        options?.onError?.(error);
      },

      onProgress(bytesUploaded, bytesTotal) {
        if (!isMountedRef.current) return;
        const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
        setUploadingVideoProgress(percentage);
      },

      onSuccess() {
        if (!isMountedRef.current) return;
        setUploadingVideo(false);
        setUploadingVideoProgress(null);
        options?.onSuccess?.();
      },
    });

    uploadRef.current = upload;

    // ✅ Resume previous upload if exists
    const previousUploads = await upload.findPreviousUploads();
    if (previousUploads.length > 0) {
      upload.resumeFromPreviousUpload(previousUploads[0]);
    }

    upload.start();
  };

  return {
    uploadVideo,
    uploadingVideo,
    uploadingVideoProgress,
    cancelUpload: () => uploadRef.current?.abort(),
  };
}
